import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {getBundlesFn, teamFn} from '../../lib/api';
import {useMarketplaceExperiment} from '../../contexts';
import {useMarketplaceCart} from '../MarketplaceCart';
import {Spinner} from '../Spinner';
import './TeamLicenseManager.scss';

const toCollection = (payload) => {
    if(Array.isArray(payload)){
        return payload;
    }
    return Array.isArray(payload?.data) ? payload.data : [];
};

const toPositiveNumber = (value, fallback = 0) => {
    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) && parsedValue >= 0 ? parsedValue : fallback;
};

const getMemberId = (member) => (
    member?.id ?? member?.user_id ?? member?.userId ?? member?.user?.id ?? null
);

const getMemberLabel = (member) => {
    const name = member?.name || member?.username || [member?.first_name, member?.last_name].filter(Boolean).join(' ');
    const email = String(member?.email || member?.user?.email || '').trim();
    const normalizedName = String(name || '').trim();
    if(normalizedName && email && normalizedName.toLowerCase() === email.toLowerCase()){
        return email;
    }
    return normalizedName || email || `User #${getMemberId(member) || ''}`;
};

const getMemberEmail = (member) => member?.email || member?.user?.email || '';

const getAppId = (appLicense) => appLicense?.app?.id ?? appLicense?.app_id ?? appLicense?.id ?? null;

const getBundleId = (teamBundle) => teamBundle?.id ?? teamBundle?.team_bundle_id ?? null;

const getBundleImageUrl = (bundle) => {
    const directImage = bundle?.bundle_card_img ?? bundle?.bundleCardImg ?? bundle?.bundle_card_image ?? bundle?.image_url ?? bundle?.image;
    const directImageUrl = typeof directImage === 'string' ? directImage : directImage?.url || directImage?.value;
    if(typeof directImageUrl === 'string' && directImageUrl.trim()){
        return directImageUrl.trim();
    }
    let metadata = bundle?.metadata;
    if(typeof metadata === 'string'){
        try{
            metadata = JSON.parse(metadata);
        }catch(error){
            return '';
        }
    }
    const metadataSources = [metadata, metadata?.metadata, metadata?.data, metadata?.attributes, metadata?.meta, metadata?.fields];
    for(const source of metadataSources){
        if(!source || typeof source !== 'object'){
            continue;
        }
        const entries = Array.isArray(source)
            ? source.map((entry) => [entry?.key ?? entry?.name, entry?.value])
            : Object.entries(source);
        const imageEntry = entries.find(([key]) => ['bundlecardimg', 'bundlecardimage'].includes(String(key || '').toLowerCase().replace(/[^a-z0-9]/g, '')));
        const imageValue = imageEntry?.[1];
        const imageUrl = typeof imageValue === 'string' ? imageValue : imageValue?.url || imageValue?.value;
        if(typeof imageUrl === 'string' && imageUrl.trim()){
            return imageUrl.trim();
        }
    }
    return '';
};

const getAssignedIds = (appLicense) => (
    Array.isArray(appLicense?.assignments)
        ? appLicense.assignments.map((assignment) => assignment?.user_id ?? assignment?.user?.id).filter((id) => id !== null && id !== undefined)
        : []
);

const getAssignmentKey = (teamBundleId, appId) => `${teamBundleId}:${appId}`;

const areIdsEqual = (leftIds, rightIds) => {
    const left = [...leftIds].map(String).sort();
    const right = [...rightIds].map(String).sort();
    return left.length === right.length && left.every((id, index) => id === right[index]);
};

const getErrorMessage = (error, fallback) => {
    const statusCode = error?.response?.status;
    if(statusCode === 403){
        return 'Only team owners can manage app licenses.';
    }
    if(statusCode === 422){
        return error?.response?.data?.message || 'Some selected users are not eligible or the bundle capacity was exceeded.';
    }
    return error?.response?.data?.message || error?.message || fallback;
};

const getCheckoutUrl = (payload) => payload?.checkout_url || payload?.url || payload?.data?.checkout_url || payload?.data?.url;

const TeamLicenseManager = ({members = [], currentUser, memberSlots = 0, invitationsLoading = false, onInvitationSent}) => {
    const {marketplaceEnabled} = useMarketplaceExperiment();
    const {controller: cartController} = useMarketplaceCart();
    const [teamBundles, setTeamBundles] = useState([]);
    const [availableBundles, setAvailableBundles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [draftAssignments, setDraftAssignments] = useState({});
    const [savingKeys, setSavingKeys] = useState({});
    const [saveMessages, setSaveMessages] = useState({});
    const [upgradeSelections, setUpgradeSelections] = useState({});
    const [upgradingIds, setUpgradingIds] = useState({});
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteStatus, setInviteStatus] = useState(null);
    const [inviting, setInviting] = useState(false);
    const [addingBundleIds, setAddingBundleIds] = useState({});
    const [bundleCartMessages, setBundleCartMessages] = useState({});
    const savedMessageTokens = useRef({});
    const licenseManagerRef = useRef(null);

    const visibleMembers = useMemo(() => {
        const memberEntries = Array.isArray(members) ? [...members] : [];
        const currentUserId = getMemberId(currentUser);
        if(currentUserId !== null && !memberEntries.some((member) => String(getMemberId(member)) === String(currentUserId))){
            memberEntries.push(currentUser);
        }
        return memberEntries.filter((member) => getMemberId(member) !== null);
    }, [currentUser, members]);

    const ownedBundleIds = useMemo(() => new Set(teamBundles
        .map((teamBundle) => teamBundle?.bundle?.id ?? teamBundle?.bundle_id)
        .filter((id) => id !== null && id !== undefined)
        .map(String)), [teamBundles]);
    const marketplaceBundles = marketplaceEnabled
        ? availableBundles.filter((bundle) => (
            bundle?.active !== false
            && bundle?.is_purchased !== true
            && !ownedBundleIds.has(String(bundle?.id))
            && bundle?.id !== null
            && bundle?.id !== undefined
            && String(bundle?.name || '').trim()
        ))
        : [];

    const loadLicenses = useCallback(async ({showLoading = true} = {}) => {
        if(showLoading){
            setLoading(true);
        }
        setError(null);
        try{
            const [licenseResult, bundleResult] = await Promise.allSettled([teamFn.appLicenses.list(), getBundlesFn()]);
            if(licenseResult.status !== 'fulfilled'){
                throw licenseResult.reason;
            }
            const nextTeamBundles = toCollection(licenseResult.value).filter((teamBundle) => teamBundle?.licensed_members !== null);
            setTeamBundles(nextTeamBundles);
            setAvailableBundles(bundleResult.status === 'fulfilled' ? toCollection(bundleResult.value) : []);
            setDraftAssignments(nextTeamBundles.reduce((drafts, teamBundle) => {
                const teamBundleId = getBundleId(teamBundle);
                if(teamBundleId === null){
                    return drafts;
                }
                (Array.isArray(teamBundle.apps) ? teamBundle.apps : []).forEach((appLicense) => {
                    const appId = getAppId(appLicense);
                    if(appId !== null){
                        drafts[getAssignmentKey(teamBundleId, appId)] = getAssignedIds(appLicense);
                    }
                });
                return drafts;
            }, {}));
        }catch(loadError){
            setTeamBundles([]);
            setAvailableBundles([]);
            setError(getErrorMessage(loadError, 'Unable to load your team app licenses.'));
        }finally{
            if(showLoading){
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        loadLicenses();
    }, [loadLicenses]);

    useEffect(() => {
        const root = licenseManagerRef.current;
        if(!root){
            return undefined;
        }
        const tableWraps = Array.from(root.querySelectorAll('.team-licenses__table-wrap'));
        const updateOverflowHints = () => {
            tableWraps.forEach((tableWrap) => {
                tableWrap.dataset.hasOverflow = tableWrap.scrollWidth > tableWrap.clientWidth + 1 ? 'true' : 'false';
            });
        };
        updateOverflowHints();
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(updateOverflowHints);
        tableWraps.forEach((tableWrap) => observer?.observe(tableWrap));
        window.addEventListener('resize', updateOverflowHints);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', updateOverflowHints);
        };
    }, [loading, teamBundles]);

    const handleToggle = (teamBundleId, appLicense, userId) => {
        const appId = getAppId(appLicense);
        if(appId === null){
            return;
        }
        const key = getAssignmentKey(teamBundleId, appId);
        setDraftAssignments((currentDrafts) => {
            const currentIds = currentDrafts[key] || getAssignedIds(appLicense);
            const isAssigned = currentIds.some((id) => String(id) === String(userId));
            const nextIds = isAssigned
                ? currentIds.filter((id) => String(id) !== String(userId))
                : [...currentIds, userId];
            if(!isAssigned && nextIds.length > toPositiveNumber(appLicense?.licenses_total)){
                return currentDrafts;
            }
            return {...currentDrafts, [key]: nextIds};
        });
        savedMessageTokens.current[key] = null;
        setSaveMessages((currentMessages) => ({...currentMessages, [key]: null}));
    };

    const handleSave = async (teamBundleId, appLicense) => {
        const appId = getAppId(appLicense);
        const key = getAssignmentKey(teamBundleId, appId);
        const userIds = draftAssignments[key] || [];
        setSavingKeys((currentKeys) => ({...currentKeys, [key]: true}));
        setSaveMessages((currentMessages) => ({...currentMessages, [key]: null}));
        try{
            await teamFn.appLicenses.updateAssignments(teamBundleId, appId, userIds);
            const savedToken = Date.now();
            savedMessageTokens.current[key] = savedToken;
            setSaveMessages((currentMessages) => ({...currentMessages, [key]: 'Saved'}));
            setTimeout(() => {
                if(savedMessageTokens.current[key] === savedToken){
                    savedMessageTokens.current[key] = null;
                    setSaveMessages((currentMessages) => currentMessages[key] === 'Saved'
                        ? {...currentMessages, [key]: null}
                        : currentMessages);
                }
            }, 2200);
            await loadLicenses({showLoading: false});
        }catch(saveError){
            setSaveMessages((currentMessages) => ({...currentMessages, [key]: getErrorMessage(saveError, 'Unable to save this assignment.')}));
        }finally{
            setSavingKeys((currentKeys) => {
                const nextKeys = {...currentKeys};
                delete nextKeys[key];
                return nextKeys;
            });
        }
    };

    const getUpgradeTiers = (teamBundle) => {
        const bundleId = teamBundle?.bundle?.id ?? teamBundle?.bundle_id;
        const bundle = availableBundles.find((entry) => String(entry?.id) === String(bundleId));
        const currentMembers = toPositiveNumber(teamBundle?.licensed_members);
        const currentPrice = toPositiveNumber(teamBundle?.license_list_price ?? teamBundle?.price ?? teamBundle?.bundle?.price);
        return (Array.isArray(bundle?.license_tiers) ? bundle.license_tiers : [])
            .filter((tier) => tier?.active !== false && toPositiveNumber(tier?.member_count) > currentMembers && toPositiveNumber(tier?.price) > currentPrice)
            .sort((left, right) => toPositiveNumber(left.member_count) - toPositiveNumber(right.member_count));
    };

    const handleUpgrade = async (teamBundle) => {
        const teamBundleId = getBundleId(teamBundle);
        const selectedTierId = upgradeSelections[teamBundleId];
        if(teamBundleId === null || !selectedTierId){
            return;
        }
        setUpgradingIds((currentIds) => ({...currentIds, [teamBundleId]: true}));
        setError(null);
        try{
            const result = await teamFn.appLicenses.checkoutUpgrade(teamBundleId, selectedTierId, 'stripe');
            const checkoutUrl = getCheckoutUrl(result);
            if(checkoutUrl){
                window.location.assign(checkoutUrl);
            }else{
                await loadLicenses({showLoading: false});
            }
        }catch(upgradeError){
            setError(getErrorMessage(upgradeError, 'Unable to start the license upgrade checkout.'));
        }finally{
            setUpgradingIds((currentIds) => {
                const nextIds = {...currentIds};
                delete nextIds[teamBundleId];
                return nextIds;
            });
        }
    };

    const handleInviteMember = async (event) => {
        event.preventDefault();
        const email = inviteEmail.trim();
        if(!email || inviting || memberSlots < 1){
            return;
        }
        setInviting(true);
        setInviteStatus(null);
        try{
            const response = await teamFn.invitations.invite(email);
            setInviteEmail('');
            setInviteStatus({type: 'success', message: response?.message || 'Invitation sent.'});
            onInvitationSent?.();
        }catch(inviteError){
            setInviteStatus({type: 'error', message: inviteError?.response?.data?.message || inviteError?.message || 'Unable to send this invitation.'});
        }finally{
            setInviting(false);
        }
    };

    const handleAddBundle = async (bundle) => {
        const bundleId = bundle?.id;
        const price = toPositiveNumber(bundle?.price ?? bundle?.license_tiers?.find((tier) => tier?.active !== false)?.price);
        setAddingBundleIds((current) => ({...current, [bundleId]: true}));
        setBundleCartMessages((current) => ({...current, [bundleId]: null}));
        try{
            await cartController.addItem({
                type: 'bundle',
                bundleId,
                display: {name: bundle?.name, amount: price, currency: 'USD'},
                sourceContext: {explicitPurchase: true},
            });
            setBundleCartMessages((current) => ({...current, [bundleId]: 'Added to cart'}));
        }catch(cartError){
            setBundleCartMessages((current) => ({...current, [bundleId]: cartError?.message || 'Unable to add this bundle.'}));
        }finally{
            setAddingBundleIds((current) => {
                const next = {...current};
                delete next[bundleId];
                return next;
            });
        }
    };

    const getBundleDisplayName = (teamBundle) => teamBundle?.bundle?.name || teamBundle?.bundle_name || 'Team bundle';

    if(loading){
        return (
            <section className="team-licenses row-section" aria-label="App license management">
                <div className="title">App licenses</div>
                <div className="team-licenses__loading"><Spinner /> Loading app licenses...</div>
            </section>
        );
    }

    return (
        <section className="team-licenses row-section" aria-label="App license management" ref={licenseManagerRef}>
            <div className="team-licenses__heading">
                <div>
                    <div className="title">App license management</div>
                    <p className="team-licenses__intro">Assign each app license to the people who need it. Your own account is included as a team member.</p>
                </div>
                <div className="team-licenses__legend"><span className="team-licenses__legend-dot"></span> Changes are saved per app</div>
            </div>

            {error &&
                <div className="team-licenses__alert" role="alert">
                    <span>{error}</span>
                    <button type="button" className="btn btn-secondary" onClick={() => loadLicenses()}>Retry</button>
                </div>
            }

            {!error && teamBundles.length === 0 &&
                <div className="team-licenses__empty">
                    <div className="team-licenses__empty-mark">＋</div>
                    <div>
                        <div className="team-licenses__empty-title">No multi-user app bundles yet</div>
                        <p>When your team owns a multi-user bundle, its licenses will appear here for assignment.</p>
                        <a href="/register" className="btn">Browse bundles</a>
                    </div>
                </div>
            }

            {teamBundles.map((teamBundle) => {
                const teamBundleId = getBundleId(teamBundle);
                const apps = Array.isArray(teamBundle.apps) ? teamBundle.apps : [];
                const licensedMembers = toPositiveNumber(teamBundle.licensed_members);
                const assignedCount = apps.reduce((total, appLicense) => total + Math.max(0, toPositiveNumber(appLicense?.licenses_assigned, getAssignedIds(appLicense).length)), 0);
                const upgradeTiers = getUpgradeTiers(teamBundle);
                return (
                    <article className="team-licenses__bundle" key={teamBundleId}>
                        <header className="team-licenses__bundle-header">
                            <div>
                                <div className="team-licenses__eyebrow">LICENSE POOL</div>
                                <h3>{getBundleDisplayName(teamBundle)}</h3>
                                <p>{licensedMembers} member {licensedMembers === 1 ? 'license' : 'licenses'} · {apps.length} {apps.length === 1 ? 'app' : 'apps'}</p>
                            </div>
                            <div className="team-licenses__bundle-stats">
                                <strong>{assignedCount}</strong>
                                <span>assignments</span>
                            </div>
                        </header>

                        {apps.length === 0 && <p className="team-licenses__note">This bundle has no app licenses available for assignment.</p>}
                        {apps.length > 0 &&
                            <div className="team-licenses__table-wrap" role="region" tabIndex={0} aria-label={`${getBundleDisplayName(teamBundle)} assignment matrix`}>
                                <table className="team-licenses__table" style={{minWidth: `${240 + (apps.length * 184)}px`}} aria-label={`${getBundleDisplayName(teamBundle)} license assignments`}>
                                    <colgroup>
                                        <col style={{width: '240px'}} />
                                        <col span={apps.length} />
                                    </colgroup>
                                    <thead>
                                        <tr>
                                            <th scope="col" className="team-licenses__member-head">TEAM MEMBER</th>
                                            {apps.map((appLicense) => {
                                                const appId = getAppId(appLicense);
                                                const key = getAssignmentKey(teamBundleId, appId);
                                                const assignedIds = draftAssignments[key] || getAssignedIds(appLicense);
                                                const total = toPositiveNumber(appLicense?.licenses_total);
                                                const isDirty = !areIdsEqual(assignedIds, getAssignedIds(appLicense));
                                                const isSaving = !!savingKeys[key];
                                                const saveError = saveMessages[key] && saveMessages[key] !== 'Saved' ? saveMessages[key] : null;
                                                const showSaveButton = isDirty || isSaving || saveMessages[key] === 'Saved' || !!saveError;
                                                return (
                                                    <th scope="col" className="team-licenses__app-head" key={appId}>
                                                        <div className="team-licenses__app-name">{appLicense?.app?.name || appLicense?.app?.slug || 'App'}</div>
                                                        <div className="team-licenses__app-capacity">{assignedIds.length}/{total} assigned</div>
                                                        <div className="team-licenses__save-slot" aria-live="polite">
                                                            {showSaveButton &&
                                                                <button type="button" className="team-licenses__save" disabled={!isDirty || isSaving} onClick={() => handleSave(teamBundleId, appLicense)}>
                                                                    {isSaving ? 'Saving…' : (saveMessages[key] === 'Saved' ? 'Saved' : 'Save changes')}
                                                                </button>
                                                            }
                                                            {saveError && <div className="team-licenses__cell-error" title={saveError} role="alert">{saveError}</div>}
                                                        </div>
                                                    </th>
                                                );
                                            })}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {visibleMembers.map((member) => {
                                            const memberId = getMemberId(member);
                                            const isOwner = String(memberId) === String(getMemberId(currentUser));
                                            return (
                                                <tr key={memberId}>
                                                    <th scope="row" className="team-licenses__member-cell">
                                                        <div className="team-licenses__member-primary">
                                                            <span className="team-licenses__member-name" title={getMemberLabel(member)}>{getMemberLabel(member)}</span>
                                                            {isOwner && <span className="team-licenses__owner-badge">OWNER</span>}
                                                        </div>
                                                        {getMemberEmail(member) && getMemberEmail(member).toLowerCase() !== getMemberLabel(member).toLowerCase() && <span className="team-licenses__member-email">{getMemberEmail(member)}</span>}
                                                    </th>
                                                    {apps.map((appLicense) => {
                                                        const appId = getAppId(appLicense);
                                                        const key = getAssignmentKey(teamBundleId, appId);
                                                        const assignedIds = draftAssignments[key] || getAssignedIds(appLicense);
                                                        const isAssigned = assignedIds.some((id) => String(id) === String(memberId));
                                                        const capacityReached = assignedIds.length >= toPositiveNumber(appLicense?.licenses_total);
                                                        return (
                                                            <td className={`team-licenses__check-cell ${isAssigned ? 'is-assigned' : ''}`} key={appId}>
                                                                <label className="team-licenses__checkbox-label">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={isAssigned}
                                                                        disabled={!isAssigned && capacityReached}
                                                                        onChange={() => handleToggle(teamBundleId, appLicense, memberId)}
                                                                        aria-label={`${isAssigned ? 'Remove' : 'Assign'} ${appLicense?.app?.name || 'app'} ${getMemberLabel(member)}`}
                                                                    />
                                                                    <span className="team-licenses__checkbox-visual" aria-hidden="true">{isAssigned ? '✓' : ''}</span>
                                                                </label>
                                                            </td>
                                                        );
                                                    })}
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                <div className="team-licenses__table-hint">Scroll horizontally to view all apps <span aria-hidden="true">→</span></div>
                            </div>
                        }

                        {upgradeTiers.length > 0 &&
                            <div className="team-licenses__upgrade">
                                <div>
                                    <div className="team-licenses__upgrade-title">Need more seats?</div>
                                    <p>Increase this license pool. The final price is calculated by the server at checkout.</p>
                                </div>
                                <div className="team-licenses__upgrade-actions">
                                    <select value={upgradeSelections[teamBundleId] || ''} onChange={(event) => setUpgradeSelections((current) => ({...current, [teamBundleId]: event.target.value}))} aria-label={`Choose a ${getBundleDisplayName(teamBundle)} license tier`}>
                                        <option value="">Choose capacity</option>
                                        {upgradeTiers.map((tier) => <option value={tier.id} key={tier.id}>{tier.member_count} members · ${toPositiveNumber(tier.price).toFixed(2)}</option>)}
                                    </select>
                                    <button type="button" className="btn" disabled={!upgradeSelections[teamBundleId] || !!upgradingIds[teamBundleId]} onClick={() => handleUpgrade(teamBundle)}>
                                        {upgradingIds[teamBundleId] ? 'Opening checkout…' : 'Upgrade licenses'}
                                    </button>
                                </div>
                            </div>
                        }
                    </article>
                );
            })}

            {visibleMembers.length > 1 && memberSlots > 0 && !invitationsLoading &&
                <form className="team-licenses__invite" onSubmit={handleInviteMember}>
                    <div>
                        <div className="team-licenses__invite-title">Invite a teammate</div>
                        <p>{memberSlots} {memberSlots === 1 ? 'member slot' : 'member slots'} available</p>
                    </div>
                    <div className="team-licenses__invite-controls">
                        <input
                            type="email"
                            value={inviteEmail}
                            onChange={(event) => {
                                setInviteEmail(event.target.value);
                                setInviteStatus(null);
                            }}
                            placeholder="name@example.com"
                            aria-label="Teammate email address"
                            autoComplete="email"
                            required
                        />
                        <button type="submit" className="btn" disabled={inviting || !inviteEmail.trim()}>
                            {inviting ? 'Sending…' : 'Send invite'}
                        </button>
                    </div>
                    {inviteStatus && <div className={`team-licenses__invite-status is-${inviteStatus.type}`} role={inviteStatus.type === 'error' ? 'alert' : 'status'}>{inviteStatus.message}</div>}
                </form>
            }

            {marketplaceBundles.length > 0 &&
                <section className="team-licenses__marketplace" aria-label="More bundles">
                    <div className="team-licenses__marketplace-heading">
                        <div>
                            <div className="title">More bundles for your team</div>
                            <p>Explore plugin bundles that your team does not own yet.</p>
                        </div>
                    </div>
                    <div className="team-licenses__bundle-cards">
                        {marketplaceBundles.map((bundle) => {
                            const bundleId = bundle.id;
                            const firstTier = (Array.isArray(bundle.license_tiers) ? bundle.license_tiers : [])
                                .filter((tier) => tier?.active !== false)
                                .sort((left, right) => toPositiveNumber(left?.price) - toPositiveNumber(right?.price))[0];
                            const price = toPositiveNumber(bundle.price ?? firstTier?.price);
            const apps = Array.isArray(bundle.apps) ? bundle.apps : [];
            const description = String(bundle.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
            const imageUrl = getBundleImageUrl(bundle);
            return (
                <article className="team-licenses__marketplace-card" key={bundleId}>
                    {imageUrl && <img className="team-licenses__marketplace-card-image" src={imageUrl} alt="" loading="lazy" />}
                    <div className="team-licenses__marketplace-card-main">
                                        <h3>{bundle.name}</h3>
                                        <p>{description || `${apps.length} ${apps.length === 1 ? 'plugin' : 'plugins'} · Lifetime access`}</p>
                                        {apps.length > 0 && <div className="team-licenses__marketplace-card-apps">{apps.slice(0, 3).map((app) => app?.name).filter(Boolean).join(' · ')}{apps.length > 3 ? ` +${apps.length - 3} more` : ''}</div>}
                                    </div>
                    <div className="team-licenses__marketplace-card-footer">
                        <strong>{firstTier ? 'From ' : ''}{new Intl.NumberFormat('en-US', {style: 'currency', currency: 'USD'}).format(price)}</strong>
                    </div>
                    <button type="button" className="team-licenses__add-to-cart" disabled={!!addingBundleIds[bundleId]} onClick={() => handleAddBundle(bundle)}>
                        {addingBundleIds[bundleId] ? 'Adding…' : 'Add to cart'}
                    </button>
                                    {bundleCartMessages[bundleId] && <div className="team-licenses__marketplace-card-status" role="status">{bundleCartMessages[bundleId]}</div>}
                                </article>
                            );
                        })}
                    </div>
                </section>
            }
        </section>
    );
};

export default TeamLicenseManager;
